import React, { useState, useEffect } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableHead,
  TableRow, Switch, TextField, Button, CircularProgress, Alert, IconButton,
  Tooltip, Snackbar, Skeleton,
} from "@mui/material";
import DeleteRoundedIcon          from "@mui/icons-material/DeleteRounded";
import AddRoundedIcon             from "@mui/icons-material/AddRounded";
import SettingsRoundedIcon        from "@mui/icons-material/SettingsRounded";
import CheckCircleRoundedIcon     from "@mui/icons-material/CheckCircleRounded";
import MapRoundedIcon             from "@mui/icons-material/MapRounded";
import SaveRoundedIcon            from "@mui/icons-material/SaveRounded";
import { getData, putData, API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B",
};

// ── Sub-tab pill ──────────────────────────────────────────────────────────
const TabPill = ({ label, icon, active, onClick }) => (
  <Box onClick={onClick} sx={{
    display:"flex", alignItems:"center", gap:1,
    px:2.5, py:1.2, borderRadius:"10px", cursor:"pointer",
    bgcolor:active?T.teal:"transparent", color:active?"#fff":T.muted,
    fontWeight:active?700:500, fontSize:13, transition:"all 0.15s",
    "&:hover":{ bgcolor:active?T.teal:T.tealLt, color:active?"#fff":T.teal },
    fontFamily:"'Plus Jakarta Sans',sans-serif",
  }}>
    {React.cloneElement(icon, { sx:{ fontSize:16 } })}
    {label}
  </Box>
);

// ── Section card ──────────────────────────────────────────────────────────
const SCard = ({ children, accent, sx={} }) => (
  <Card sx={{ borderRadius:"16px", border:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.06)", overflow:"hidden", ...sx }}>
    {accent && <Box sx={{ height:3, background:`linear-gradient(90deg,${accent},${accent}66)` }} />}
    {children}
  </Card>
);

// ── Main component ────────────────────────────────────────────────────────
export default function EwbConfiguration() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* Hero header */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 100%)`, borderRadius:"20px", p:3, mb:3,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex", alignItems:"center", gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <SettingsRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>EWB Configuration</Typography>
            <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
              EWB-001 · Decision-logic for E-Way Bill flow · changes apply on next booking, cached ~5 min server-side
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tab toggle */}
      <Box sx={{ display:"flex", gap:0.5, bgcolor:"white", border:`1px solid ${T.border}`, borderRadius:"14px", p:0.75, mb:3, width:"fit-content" }}>
        <TabPill label="Exempt Goods (HSN map)" icon={<CheckCircleRoundedIcon />} active={tab===0} onClick={()=>setTab(0)} />
        <TabPill label="State Thresholds"       icon={<MapRoundedIcon />}          active={tab===1} onClick={()=>setTab(1)} />
      </Box>

      {tab===0 ? <ExemptGoodsTab /> : <StateThresholdsTab />}
    </Box>
  );
}

// ── Tab 1: Exempt Goods ───────────────────────────────────────────────────
function ExemptGoodsTab() {
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [snack,    setSnack]    = useState({ open:false, msg:"", sev:"success" });

  const toast = (msg, sev="success") => setSnack({ open:true, msg, sev });

  const load = async () => {
    setLoading(true);
    try {
      const res = await getData(API_ENDPOINTS.ewbAdminExemptGoods);
      setRows(res?.result?.categories || []);
    } catch(e) { toast(e.response?.data?.message||e.message, "error"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (row, field) => {
    setSavingId(row.hsn_category_id);
    try {
      await putData(API_ENDPOINTS.ewbAdminExemptGoods, {
        updates: [{ hsn_category_id: row.hsn_category_id, [field]: !row[field] }],
      });
      setRows(prev => prev.map(r => r.hsn_category_id===row.hsn_category_id ? { ...r, [field]:!row[field] } : r));
      toast(`Updated "${row.display_name}"`);
    } catch(e) { toast(e.response?.data?.message||e.message, "error"); }
    setSavingId(null);
  };

  return (
    <>
      <SCard accent={T.teal}>
        <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1.5, mb:2 }}>
            <Box sx={{ width:30,height:30,borderRadius:"8px",bgcolor:T.tealLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
              <CheckCircleRoundedIcon sx={{ fontSize:16,color:T.teal }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Exempt Goods (HSN Map)</Typography>
              <Typography sx={{ fontSize:12,color:T.muted }}>
                {loading ? "Loading…" : `${rows.length} HSN categories`} · Flip <strong>Exempt</strong> to skip EWB section for that category
              </Typography>
            </Box>
          </Box>

          {loading ? (
            Array.from({length:6}).map((_,i)=>(
              <Box key={i} sx={{ display:"flex",alignItems:"center",gap:2,py:1.2,borderBottom:`1px solid ${T.border}` }}>
                <Skeleton width={220} height={16} />
                <Skeleton width={80} height={16} sx={{ ml:"auto" }} />
                <Skeleton variant="rounded" width={44} height={24} />
                <Skeleton variant="rounded" width={44} height={24} />
              </Box>
            ))
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& th":{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.4px",py:1.5,borderBottom:`2px solid ${T.border}` } }}>
                  <TableCell>Category</TableCell>
                  <TableCell>HSN Code</TableCell>
                  <TableCell align="center">Exempt</TableCell>
                  <TableCell align="center">Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => {
                  const busy = savingId===row.hsn_category_id;
                  return (
                    <TableRow key={row.hsn_category_id} hover sx={{ "&:last-child td":{ border:0 },"&:hover":{ bgcolor:T.tealLt+"22" } }}>
                      <TableCell>
                        <Typography sx={{ fontSize:13,fontWeight:500,color:T.text }}>{row.display_name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display:"inline-flex",px:1,py:0.2,bgcolor:T.amberLt,borderRadius:"6px",border:`1px solid ${T.amber}22` }}>
                          <Typography sx={{ fontSize:11,fontWeight:700,color:T.amber,fontFamily:"monospace" }}>{row.hsn_code}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={row.is_exempt?"Click to un-exempt":"Click to exempt this category"}>
                          <Switch
                            size="small" checked={!!row.is_exempt} disabled={busy}
                            onChange={()=>toggle(row,"is_exempt")}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": { color:T.indigo },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor:T.indigo },
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small" checked={!!row.is_active} disabled={busy}
                          onChange={()=>toggle(row,"is_active")}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color:T.teal },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor:T.teal },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </SCard>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} variant="filled" onClose={()=>setSnack(s=>({...s,open:false}))} sx={{ borderRadius:"12px" }}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}

// ── Tab 2: State Thresholds ───────────────────────────────────────────────
function StateThresholdsTab() {
  const [defaultThreshold, setDefaultThreshold] = useState("");
  const [overrides,        setOverrides]        = useState({});
  const [loading,          setLoading]          = useState(true);
  const [saving,           setSaving]           = useState(false);
  const [snack,            setSnack]            = useState({ open:false, msg:"", sev:"success" });
  const [newPair,          setNewPair]          = useState({ key:"", value:"" });

  const toast = (msg, sev="success") => setSnack({ open:true, msg, sev });

  const load = async () => {
    setLoading(true);
    try {
      const res = await getData(API_ENDPOINTS.ewbAdminStateThresholds);
      setDefaultThreshold(String(res?.result?.ewb_default_threshold ?? 50000));
      setOverrides(res?.result?.ewb_state_pair_thresholds || {});
    } catch(e) { toast(e.response?.data?.message||e.message, "error"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addOverride = () => {
    if (!/^[A-Z]{2}:[A-Z]{2}$/i.test(newPair.key)) {
      toast("Key must be ORIGIN:DEST (e.g. TS:KA)", "error"); return;
    }
    const v = Number(newPair.value);
    if (!Number.isFinite(v) || v < 0) { toast("Threshold must be ≥ 0", "error"); return; }
    setOverrides(p => ({ ...p, [newPair.key.toUpperCase()]:v }));
    setNewPair({ key:"", value:"" });
  };

  const removeOverride = key => setOverrides(p => { const n={...p}; delete n[key]; return n; });

  const save = async () => {
    setSaving(true);
    try {
      const def = Number(defaultThreshold);
      if (!Number.isFinite(def)||def<0) throw new Error("Default must be ≥ 0");
      const sanitised = Object.fromEntries(Object.entries(overrides).map(([k,v])=>[k,Number(v)]));
      await putData(API_ENDPOINTS.ewbAdminStateThresholds, { ewb_default_threshold:def, ewb_state_pair_thresholds:sanitised });
      toast("Thresholds saved successfully!");
    } catch(e) { toast(e.response?.data?.message||e.message, "error"); }
    setSaving(false);
  };

  if (loading) return <Box sx={{ display:"flex",justifyContent:"center",py:6 }}><CircularProgress sx={{ color:T.teal }} /></Box>;

  const entries = Object.entries(overrides);

  return (
    <>
      {/* Default threshold */}
      <SCard accent={T.teal} sx={{ mb:3 }}>
        <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1.2, mb:2 }}>
            <Box sx={{ width:30,height:30,borderRadius:"8px",bgcolor:T.tealLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
              <MapRoundedIcon sx={{ fontSize:16,color:T.teal }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Default Threshold (₹)</Typography>
              <Typography sx={{ fontSize:12,color:T.muted }}>Used when no explicit state-pair override exists. Statutory default is ₹50,000.</Typography>
            </Box>
          </Box>
          <TextField
            type="number" value={defaultThreshold}
            onChange={e=>setDefaultThreshold(e.target.value)}
            size="small"
            InputProps={{ startAdornment:<Typography sx={{ fontSize:13,color:T.muted,mr:0.5 }}>₹</Typography> }}
            sx={{ width:220, "& .MuiOutlinedInput-root":{ borderRadius:"12px" } }}
          />
        </CardContent>
      </SCard>

      {/* Per-state-pair overrides */}
      <SCard accent={T.indigo} sx={{ mb:3 }}>
        <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
          <Box sx={{ display:"flex", alignItems:"center", gap:1.2, mb:2 }}>
            <Box sx={{ width:30,height:30,borderRadius:"8px",bgcolor:T.indigoLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
              <MapRoundedIcon sx={{ fontSize:16,color:T.indigo }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize:14,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Per-State-Pair Overrides</Typography>
              <Typography sx={{ fontSize:12,color:T.muted }}>Override the default for a specific origin → destination state pair (e.g. Tamil Nadu, Maharashtra)</Typography>
            </Box>
          </Box>

          {entries.length === 0 ? (
            <Box sx={{ py:3, textAlign:"center", bgcolor:T.bg, borderRadius:"12px", border:`1px solid ${T.border}` }}>
              <Typography sx={{ fontSize:13,color:T.muted }}>No overrides — all state pairs use the default threshold</Typography>
            </Box>
          ) : (
            <Table size="small" sx={{ mb:2 }}>
              <TableHead>
                <TableRow sx={{ "& th":{ fontWeight:700,fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.4px",py:1.5,borderBottom:`2px solid ${T.border}` } }}>
                  <TableCell>State Pair</TableCell>
                  <TableCell>Threshold (₹)</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map(([key,val]) => (
                  <TableRow key={key} hover sx={{ "&:last-child td":{ border:0 } }}>
                    <TableCell>
                      <Box sx={{ display:"inline-flex",px:1.2,py:0.4,bgcolor:T.indigoLt,borderRadius:"8px",border:`1px solid ${T.indigo}22` }}>
                        <Typography sx={{ fontSize:12,fontWeight:800,color:T.indigo,fontFamily:"monospace" }}>{key}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small" type="number" value={val}
                        onChange={e=>setOverrides(p=>({ ...p,[key]:e.target.value }))}
                        sx={{ width:160, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }}
                        InputProps={{ startAdornment:<Typography sx={{ fontSize:13,color:T.muted,mr:0.5 }}>₹</Typography> }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove override">
                        <IconButton size="small" onClick={()=>removeOverride(key)}
                          sx={{ color:T.rose, "&:hover":{ bgcolor:T.roseLt } }}>
                          <DeleteRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Add new override */}
          <Box sx={{ display:"flex", gap:1.5, alignItems:"center", mt:2, p:2, bgcolor:T.bg, borderRadius:"12px", border:`1px solid ${T.border}` }}>
            <TextField
              size="small" placeholder="TS:KA" value={newPair.key}
              onChange={e=>setNewPair(p=>({ ...p,key:e.target.value.toUpperCase() }))}
              label="State pair"
              sx={{ width:160, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }}
            />
            <TextField
              size="small" type="number" placeholder="50000" value={newPair.value}
              onChange={e=>setNewPair(p=>({ ...p,value:e.target.value }))}
              label="Threshold (₹)"
              sx={{ width:160, "& .MuiOutlinedInput-root":{ borderRadius:"10px" } }}
            />
            <Button variant="contained" disableElevation startIcon={<AddRoundedIcon />} onClick={addOverride}
              sx={{ borderRadius:"10px", textTransform:"none", fontWeight:700, bgcolor:T.indigo, "&:hover":{ bgcolor:"#3730A3" } }}>
              Add
            </Button>
          </Box>
        </CardContent>
      </SCard>

      {/* Save */}
      <Box sx={{ display:"flex", justifyContent:"flex-end" }}>
        <Button variant="contained" disableElevation onClick={save} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
          sx={{ borderRadius:"12px",textTransform:"none",fontWeight:700,fontSize:14,px:4,py:1.4, bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk },boxShadow:`0 4px 14px rgba(0,124,132,0.3)` }}>
          {saving ? "Saving…" : "Save Thresholds"}
        </Button>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} variant="filled" onClose={()=>setSnack(s=>({...s,open:false}))} sx={{ borderRadius:"12px" }}>{snack.msg}</Alert>
      </Snackbar>
    </>
  );
}
